<template>
  <div class="patient-tags-container">
    <!-- Skeleton Loading -->
    <template v-if="isLoading">
      <div v-for="i in 3" :key="'skeleton-' + i" class="tag-skeleton" :style="{ width: [52, 68, 44][i - 1] + 'px' }" />
    </template>

    <!-- Tag Pills -->
    <template v-else v-for="(tag, index) in visibleTags" :key="tag.patient_flag_id">
      <el-tooltip
        content="Click to view patients under the same tag."
        placement="bottom"
        :show-after="300"
        :enterable="false"
      >
        <div
          class="tag-pill"
          :ref="el => { if (el) tagPillRefs[tag.patient_flag_id] = el; }"
          @mouseenter="hoveredTagId = tag.patient_flag_id"
          @mouseleave="hoveredTagId = null"
          @click="handleTagClick(tag)"
        >
          <svg
            class="tag-people-icon"
            :class="{ 'visible': hoveredTagId === tag.patient_flag_id }"
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path d="M8.335 6.565C9.02 7.03 9.5 7.66 9.5 8.5V10H11.5V8.5C11.5 7.41 9.715 6.765 8.335 6.565Z" fill="#374957"/>
            <path d="M7.5 6C8.605 6 9.5 5.105 9.5 4C9.5 2.895 8.605 2 7.5 2C7.265 2 7.045 2.05 6.835 2.12C7.25 2.635 7.5 3.29 7.5 4C7.5 4.71 7.25 5.365 6.835 5.88C7.045 5.95 7.265 6 7.5 6Z" fill="#374957"/>
            <path d="M4.5 6C5.605 6 6.5 5.105 6.5 4C6.5 2.895 5.605 2 4.5 2C3.395 2 2.5 2.895 2.5 4C2.5 5.105 3.395 6 4.5 6ZM4.5 3C5.05 3 5.5 3.45 5.5 4C5.5 4.55 5.05 5 4.5 5C3.95 5 3.5 4.55 3.5 4C3.5 3.45 3.95 3 4.5 3Z" fill="#374957"/>
            <path d="M4.5 6.5C3.165 6.5 0.5 7.17 0.5 8.5V10H8.5V8.5C8.5 7.17 5.835 6.5 4.5 6.5ZM7.5 9H1.5V8.505C1.6 8.145 3.15 7.5 4.5 7.5C5.85 7.5 7.4 8.145 7.5 8.5V9Z" fill="#374957"/>
          </svg>
          <span class="tag-name">{{ formatTagName(tag.patient_flag_name) }}</span>
          <button
            class="remove-btn"
            :class="{ 'visible': hoveredTagId === tag.patient_flag_id }"
            @click.stop="handleRemoveTag(tag)"
            :aria-label="`Remove tag ${tag.patient_flag_name}`"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M0.8 0.8L7.2 7.2M0.8 7.2L7.2 0.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </el-tooltip>
    </template>

    <!-- More Tags Indicator -->
    <span
      v-if="hiddenTagsCount > 0 && !isExpanded"
      class="more-tags-link"
      @click="toggleExpand"
    >
      {{ hiddenTagsCount }} more tags
    </span>
    <span
      v-if="isExpanded && tags.length > collapsedLimit"
      class="more-tags-link"
      @click="toggleExpand"
    >
      collapse
    </span>

    <!-- Inline Input (slides out when isInputVisible) -->
    <div class="inline-input-wrapper" :class="{ 'expanded': isInputVisible }">
      <input
        v-if="isInputVisible"
        ref="tagSearchInput"
        v-model="searchQuery"
        type="text"
        class="inline-tag-input"
        placeholder="Type to tag the patient ..."
        @input="handleSearchInput"
        @focus="showDropdown = true"
        @blur="handleInputBlur"
        @keydown.down.prevent="navigateDown"
        @keydown.up.prevent="navigateUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc="closeInput"
      />

      <!-- Dropdown for existing tags -->
      <div v-if="isInputVisible && showDropdown" class="tags-dropdown">
        <div class="dropdown-header">Existing Tags</div>
        <div class="dropdown-list" v-if="!isLoadingCatalog">
          <div
            v-for="(tag, index) in filteredCatalogTags"
            :key="tag.patient_flag_id"
            class="dropdown-item"
            :class="{ 'highlighted': highlightedIndex === index }"
            @mousedown.prevent="selectTag(tag)"
            @mouseenter="highlightedIndex = index"
          >
            {{ formatTagName(tag.patient_flag_name) }}
          </div>

          <!-- Create new tag option -->
          <div
            v-if="showCreateOption"
            class="dropdown-item create-option"
            :class="{ 'highlighted': highlightedIndex === filteredCatalogTags.length }"
            @mousedown.prevent="createNewTag"
            @mouseenter="highlightedIndex = filteredCatalogTags.length"
          >
            Create "{{ formatTagName(normalizeTagName(searchQuery.trim())) }}"
          </div>

          <div v-if="filteredCatalogTags.length === 0 && !showCreateOption" class="dropdown-empty">
            No tags found
          </div>
        </div>
        <div v-else class="dropdown-loading">
          Loading...
        </div>
      </div>
    </div>

    <!-- Add Tag Button -->
    <el-tooltip
      v-if="!isLoading"
      :content="isAtLimit ? 'A patient can have maximum 10 tags.' : ''"
      :disabled="!isAtLimit"
      placement="top"
    >
      <button
        class="add-tag-btn"
        :class="{ 'disabled': isAtLimit || isInputVisible }"
        :aria-disabled="isAtLimit"
        @click="handleAddTagClick"
      >
        <span class="add-icon">+</span>
        <span class="add-text">Add Tag</span>
      </button>
    </el-tooltip>

    <!-- Patient Tag List Modal -->
    <PatientTagListModal
      v-model="showTagListModal"
      :tagId="selectedTag?.patient_flag_id"
      :tagName="formatTagName(selectedTag?.patient_flag_name || '')"
      @go-to-chat="$emit('go-to-chat', $event)"
    />
  </div>
</template>

<script>
import {
  getTagCatalogApi,
  getPatientTagsApi,
  assignTagToPatientApi,
  createAndAssignTagApi,
  removeTagFromPatientApi
} from '@/api/patientTags';
import PatientTagListModal from './PatientTagListModal.vue';

export default {
  name: 'PatientTags',
  components: {
    PatientTagListModal,
  },
  props: {
    patientId: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['go-to-chat'],
  data() {
    return {
      tags: [],
      catalogTags: [],
      isLoading: false,
      isLoadingCatalog: false,
      hoveredTagId: null,
      isExpanded: false,
      collapsedLimit: 5,
      maxTags: 10,

      isInputVisible: false,
      showDropdown: false,
      searchQuery: '',
      highlightedIndex: 0,

      // Tag list modal
      showTagListModal: false,
      selectedTag: null,
      tagPillRefs: {},
    };
  },
  computed: {
    isAtLimit() {
      return this.tags.length >= this.maxTags;
    },
    visibleTags() {
      if (this.isExpanded) {
        return this.tags;
      }
      return this.tags.slice(0, this.collapsedLimit);
    },
    hiddenTagsCount() {
      return Math.max(0, this.tags.length - this.collapsedLimit);
    },
    filteredCatalogTags() {
      const query = this.searchQuery.trim().toLowerCase();
      const unassignedTags = this.catalogTags.filter(
        tag => !this.isTagAssigned(tag.patient_flag_id)
      );
      if (!query) {
        return unassignedTags.sort((a, b) =>
          a.patient_flag_name.toLowerCase().localeCompare(b.patient_flag_name.toLowerCase())
        );
      }
      return unassignedTags
        .filter(tag => tag.patient_flag_name.toLowerCase().includes(query))
        .sort((a, b) =>
          a.patient_flag_name.toLowerCase().localeCompare(b.patient_flag_name.toLowerCase())
        );
    },
    showCreateOption() {
      const query = this.searchQuery.trim();
      if (!query) return false;
      // Check if normalized name already exists (case-insensitive for lowercase, exact for all-caps)
      const normalized = query === query.toUpperCase() ? query : query.toLowerCase();
      const exists = this.catalogTags.some(
        tag => tag.patient_flag_name.toLowerCase() === normalized.toLowerCase()
      );
      return !exists;
    }
  },
  watch: {
    patientId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          // Clear previous patient's tags immediately to avoid showing stale data
          this.tags = [];
          this.isInputVisible = false;
          this.showDropdown = false;
          this.fetchPatientTags();
        } else {
          this.tags = [];
          this.isInputVisible = false;
          this.showDropdown = false;
        }
      }
    }
  },
  methods: {
    async fetchPatientTags() {
      if (!this.patientId) return;
      const targetPatientId = this.patientId;
      this.isLoading = true;
      try {
        const response = await getPatientTagsApi(targetPatientId);
        // Only apply result if patientId hasn't changed during the fetch
        if (this.patientId === targetPatientId) {
          this.tags = response.tags || [];
        }
      } catch (error) {
        console.error('Failed to fetch patient tags:', error);
        if (this.patientId === targetPatientId) {
          this.tags = [];
        }
      } finally {
        if (this.patientId === targetPatientId) {
          this.isLoading = false;
        }
      }
    },

    async fetchCatalogTags() {
      this.isLoadingCatalog = true;
      try {
        const response = await getTagCatalogApi();
        this.catalogTags = response.tags || [];
      } catch (error) {
        console.error('Failed to fetch catalog tags:', error);
        this.catalogTags = [];
      } finally {
        this.isLoadingCatalog = false;
      }
    },

    handleAddTagClick() {
      if (this.isAtLimit) return;
      this.isInputVisible = true;
      this.searchQuery = '';
      this.highlightedIndex = 0;
      this.fetchCatalogTags();
      this.$nextTick(() => {
        this.$refs.tagSearchInput?.focus();
      });
    },

    closeInput() {
      this.isInputVisible = false;
      this.showDropdown = false;
      this.searchQuery = '';
      this.highlightedIndex = 0;
    },

    handleInputBlur() {
      setTimeout(() => {
        this.closeInput();
      }, 150);
    },

    handleSearchInput() {
      this.highlightedIndex = 0;
      this.showDropdown = true;
    },

    isTagAssigned(tagId) {
      return this.tags.some(t => t.patient_flag_id === tagId);
    },

    navigateDown() {
      const maxIndex = this.showCreateOption
        ? this.filteredCatalogTags.length
        : this.filteredCatalogTags.length - 1;
      if (this.highlightedIndex < maxIndex) {
        this.highlightedIndex++;
      }
    },

    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },

    selectHighlighted() {
      if (this.highlightedIndex === this.filteredCatalogTags.length && this.showCreateOption) {
        this.createNewTag();
      } else if (this.filteredCatalogTags[this.highlightedIndex]) {
        this.selectTag(this.filteredCatalogTags[this.highlightedIndex]);
      }
    },

    async selectTag(tag) {
      if (this.isTagAssigned(tag.patient_flag_id)) return;
      if (this.isAtLimit) {
        this.closeInput();
        return;
      }

      this.tags.push(tag);
      this.closeInput();

      try {
        await assignTagToPatientApi(this.patientId, tag.patient_flag_id);
      } catch (error) {
        this.tags = this.tags.filter(t => t.patient_flag_id !== tag.patient_flag_id);
        if (error.response?.data?.errorMessage === 'TAG_LIMIT_REACHED') {
          await this.fetchPatientTags();
        }
      }
    },

    normalizeTagName(name) {
      // If all uppercase, keep as-is; otherwise convert to all lowercase
      if (name === name.toUpperCase()) {
        return name;
      }
      return name.toLowerCase();
    },

    async createNewTag() {
      const rawName = this.searchQuery.trim();
      if (!rawName || this.isAtLimit) return;

      const tagName = this.normalizeTagName(rawName);
      const tempTag = {
        patient_flag_id: `temp_${Date.now()}`,
        patient_flag_name: tagName
      };
      this.tags.push(tempTag);
      this.closeInput();

      try {
        const response = await createAndAssignTagApi(this.patientId, tagName);
        const index = this.tags.findIndex(t => t.patient_flag_id === tempTag.patient_flag_id);
        if (index !== -1 && response.tag) {
          this.tags.splice(index, 1, response.tag);
          if (!this.catalogTags.some(t => t.patient_flag_id === response.tag.patient_flag_id)) {
            this.catalogTags.push(response.tag);
          }
        }
      } catch (error) {
        this.tags = this.tags.filter(t => t.patient_flag_id !== tempTag.patient_flag_id);
        if (error.response?.data?.errorMessage === 'TAG_LIMIT_REACHED') {
          await this.fetchPatientTags();
        }
      }
    },

    async handleRemoveTag(tag) {
      const removedIndex = this.tags.findIndex(t => t.patient_flag_id === tag.patient_flag_id);
      const removedTag = this.tags[removedIndex];
      this.tags.splice(removedIndex, 1);

      try {
        await removeTagFromPatientApi(this.patientId, tag.patient_flag_id);
      } catch (error) {
        if (removedTag) {
          this.tags.splice(removedIndex, 0, removedTag);
        }
      }
    },

    handleTagClick(tag) {
      this.selectedTag = tag;
      this.showTagListModal = true;
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    formatTagName(name) {
      if (!name) return '';
      // If all uppercase, keep it as is (e.g., "BIG" -> "BIG")
      if (name === name.toUpperCase()) {
        return name;
      }
      // Otherwise, capitalize first letter of each word (e.g., "easy to talk" -> "Easy To Talk")
      return name.replace(/\b\w/g, char => char.toUpperCase());
    }
  }
};
</script>

<style scoped>
.patient-tags-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  position: relative;
}

/* Skeleton Loading */
.tag-skeleton {
  height: 20px;
  border-radius: 10px;
  background: linear-gradient(90deg, #e8e9ea 25%, #f2f3f4 50%, #e8e9ea 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Tag Pill - Figma: Blue-10 background, IconGrey text */
.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  height: 20px;
  background: #E4F2F5;
  border-radius: 10px;
  color: #374957;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  cursor: pointer;
  transition: padding 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
}

.tag-pill:hover {
  background: #d4e8ed;
  padding-right: 8px;
}

.tag-people-icon {
  flex-shrink: 0;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-width 0.2s ease, opacity 0.2s ease;
}

.tag-people-icon.visible {
  max-width: 12px;
  opacity: 1;
}

.tag-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Remove Button - Red X on hover */
.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 0;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-left: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #E31B0C;
  cursor: pointer;
  opacity: 0;
  overflow: hidden;
  transition: max-width 0.2s ease, opacity 0.2s ease, margin 0.2s ease, background 0.2s ease;
}

.remove-btn.visible {
  max-width: 16px;
  margin-left: 4px;
  opacity: 1;
}

.remove-btn:hover {
  background: rgba(227, 27, 12, 0.1);
}

/* More Tags Link - underlined text */
.more-tags-link {
  color: var(--VibrantDarkBlue, #004879);
  text-align: justify;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.25px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-underline-offset: auto;
  text-decoration-thickness: auto;
  text-underline-position: from-font;
  cursor: pointer;
}

.more-tags-link:hover {
  opacity: 0.8;
}

/* Inline Input Wrapper - slides out */
.inline-input-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 0;
  overflow: visible;
  transition: max-width 0.3s ease;
}

.inline-input-wrapper.expanded {
  max-width: 200px;
}

.inline-tag-input {
  width: 180px;
  padding: 0 10px;
  height: 20px;
  border: 1px solid #e8e9ea;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #374957;
  line-height: 18px;
  outline: none;
  background: white;
  box-sizing: border-box;
}

.inline-tag-input::placeholder {
  color: #999;
}

.inline-tag-input:focus {
  border-color: #004879;
}

/* Dropdown for existing tags */
.tags-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 180px;
  max-height: 200px;
  background: white;
  border: 1px solid #e8e9ea;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c4c4c4 transparent;
}

.tags-dropdown::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.tags-dropdown::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 2px;
}

.dropdown-header {
  padding: 8px 12px;
  color: var(--DividerGray, #C4C4C4);
  text-align: center;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.2px;
}

.dropdown-item {
  padding: 6px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--ContentMediumGrey, #999);
  cursor: pointer;
  transition: background 0.15s ease;
  line-height: 12px;
  letter-spacing: 0.25px;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: #E4F2F5;
}

.dropdown-item.create-option {
  color: #004879;
  font-weight: 500;
}

.dropdown-empty,
.dropdown-loading {
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* Add Tag Button - Figma style */
.add-tag-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 10px;
  height: 20px;
  border: 1px solid #004879;
  border-radius: 10px;
  background: var(--Neutral-White, #FFF);
  color: #004879;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.add-icon {
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
}

.add-tag-btn:hover:not(.disabled) {
  background: rgba(0, 72, 121, 0.08);
}

.add-tag-btn.disabled {
  border-color: #c4c4c4;
  color: #999;
  cursor: not-allowed;
}
</style>
